import axiosInstance from "../app/axios";
import PendingPayEmailProps from "../propInterfaces/pendingPayEmailProps";
import userRequestExcelProps from "../propInterfaces/userRequestExcelProps";
import UserRequestByDateListProps from "../propInterfaces/userRequestListExportProps";
import UserRequestProps from "../propInterfaces/UserRequestProps";
import { TRIP_STATUS } from "../shared/constants/TripStatus";
import ApiResponseDTO from "../shared/dtos/apiResponseDTO";
import { LocationDTO } from "../shared/dtos/PassengerGroupDTO";
import UserRequestDTO from "../shared/dtos/userRequestDTO";
import UserRequestByDateRequestDTO from "../shared/dtos/userRequestListExportRequestDTO";

export const getUserRequests = async (userRequestProps: UserRequestProps) => {
  const result = await axiosInstance.post(
    "user-request/actives",
    userRequestProps,
    {
      headers: { "x-hide-success": true },
    },
  );
  return result;
};

export const postUserRequestsAsDispatcher = async (
  userRequest: UserRequestDTO,
) => {
  const result = await axiosInstance.post("trip/dispatch", userRequest);
  return result.data.data;
};

export const removeProviderFromUserRequest = async ({
  userRequestId,
}: UserRequestProps) => {
  const result = await axiosInstance.post(
    `trip/remove/${userRequestId}/driver`,
  );
  return result.data.data;
};

export const getUserRequestsStatementBetweenTwoDates = async ({
  startDate,
  endDate,
  page,
  take,
  bookingId,
  searchText,
  shouldSendCompany,
  adminId,
  status,
}: UserRequestByDateListProps) => {
  const userRequestListExportRequestDTO: UserRequestByDateRequestDTO = {
    startDate,
    endDate,
    bookingId,
    searchText,
    adminId,
    shouldSendCompany,
    status,
  };

  const result = await axiosInstance.post(
    `user-request/report/statement/list?page=${page}&take=${take}`,
    userRequestListExportRequestDTO,
  );
  return result.data;
};

export const getPendingCalculationUserRequest = async ({
  page,
  take,
  searchText,
}: UserRequestByDateListProps) => {
  const result = await axiosInstance.post(
    `user-request/pending/calculation?page=${page}&take=${take}`,
    { searchText },
  );
  return result.data;
};

export const getUserRequestsExcel = async ({
  startDate,
  endDate,
  shouldSendCompany,
  shouldLoadCache,
  adminId,
}: userRequestExcelProps) => {
  const userRequestListExportRequestDTO: UserRequestByDateRequestDTO = {
    startDate: startDate,
    endDate: endDate,
    shouldSendCompany: shouldSendCompany,
    shouldLoadCache: shouldLoadCache,
    adminId,
  };

  const result = await axiosInstance.post(
    "user-request/report/statement/excel",
    userRequestListExportRequestDTO,
  );
  return result.data.file;
};

export const deleteUserRequest = async ({
  userRequestId,
}: UserRequestProps) => {
  const result = await axiosInstance.delete(`trip/${userRequestId}`);
  return result;
};

export const sendPaymentEmail = async ({
  userRequestId,
  total,
  currencyCode,
}: PendingPayEmailProps) => {
  const result = await axiosInstance.patch(
    `admin/trip/payment/mail/${userRequestId}`,
    { total, currencyCode },
  );
  return result;
};

export const skipPendingUserRequest = async (userRequestId: number) => {
  const result = await axiosInstance.patch(
    `admin/trip/payment/skip/${userRequestId}`,
    {},
  );
  return result;
};

export const skipAllPendingUserRequests = async (
  userRequestIds: Array<number>,
) => {
  const result = await axiosInstance.patch("admin/trip/payment/skip/all", {
    userRequestIds,
  });
  return result;
};

export const completeUserRequest = async ({
  userRequestId,
}: UserRequestProps) => {
  const result = await axiosInstance.patch(
    `admin/trip/status/${userRequestId}`,
    {
      status: TRIP_STATUS.Completed,
    },
  );
  return result;
};

export const cancelUserRequest = async ({
  userRequestId,
}: UserRequestProps) => {
  const result = await axiosInstance.get(`admin/trip/cancel/${userRequestId}`);
  return result;
};

export const getTripTrackingInformation = async (bookingId: string): Promise<LocationDTO[]> => {
  const result = await axiosInstance.get(
    `trip/coords/${bookingId}`,
  );
  return result?.data;
};

export const getUserRequestById = async ({
  userRequestId,
}: UserRequestProps) => {
  const result = await axiosInstance.get(`admin/trip/${userRequestId}`);
  return result.data;
};

export const updateQRFeeUserRequest = async ({
  userRequestId,
  qrFee,
}: UserRequestProps) => {
  const result = await axiosInstance.patch(
    `admin/trip/qrFee/${userRequestId}`,
    { qrFee },
  );
  return result.data;
};

export const updateUserRequestPayment = async ({
  userRequestId,
  total,
}: UserRequestProps) => {
  const result = await axiosInstance.patch(
    `admin/trip/payment/${userRequestId}`,
    { total },
  );
  return result.data;
};

export const updateSheduleDate = async (
  userRequestId: number,
  scheduleDate: string,
) => {
  return await axiosInstance.post(
    `user-request/scheduledate/${userRequestId}`,
    { scheduleDate },
  );
};

export const updateDescription = async (
  userRequestId: number,
  description: string,
) => {
  return await axiosInstance.post(`user-request/description/${userRequestId}`, {
    description,
  });
};

export const getUserRequestTripMap = async ({ id }: { id: string }) => {
  const result = await axiosInstance.get(`user-request/${id}/trip-map`);
  return result.data;
};

export const getAdminComission = async (
  requestLastPeriod: boolean = false,
): Promise<ApiResponseDTO<any>> => {
  const result = await axiosInstance.get(
    `user-request/admin/comission?requestLastPeriod=${requestLastPeriod}`,
  );
  return result.data;
};

export const createTripBundle = async (userRequests: UserRequestDTO[]) => {
  return await axiosInstance.post("trip/bundle", { userRequests });
};

export const generateTripLink = async (bookingId: string) => {
  const result = await axiosInstance.post("user-request/generate/link", {
    bookingId,
  });
  return result.data.data;
};

export const playManagerTrip = async (bookingId: string) => {
  const result = await axiosInstance.post("trip/play", { bookingId });
  return result.data.data;
};
